// FLAG:useDocuAi — render the KoSIT validation report as HTML. The validation-report artifact is an SVRL XML
// envelope that EMBEDS the human-readable report as a single <html data-report-type="report">…</html>
// block (produced by KoSIT's default-report.xsl). DMS (fileGet.service.ts::extractKositHtml) and DocuAI
// (documents.py::_extract_kosit_html) both slice it the same way; we mirror that slice here and render the
// HTML in a sandboxed iframe instead of showing the raw XML. Only ever mounted from the gated
// document-drawer-preview dispatch (useDocuAi + isValidationReport), so flag-OFF never reaches it.
import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import Loader from '@/modules/base/components/loader/loader'
import ThemeInterface from '@/theme-interface'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  frame: {
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: theme.colors.white,
  },
  empty: {
    width: '100%',
    padding: '20px 40px',
  },
  // The loader positions itself absolutely, so it needs a positioned box of its own.
  loaderContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
}))

interface DocumentDrawerPreviewHtmlInterface {
  data?: string // streamed bytes from the parent (a `data:<mime>;base64,…` URL, or raw text)
  // Whether the parent is still fetching those bytes, and whether that fetch failed. Without them
  // this component cannot tell "not there yet" from "will never be there" — see the comment on the
  // render branches below.
  isLoading?: boolean
  hasError?: boolean
}

// Decode the streamed report bytes to text. streamDmsFile resolves a `data:<mime>;base64,…` URL
// (FileReader.readAsDataURL); fall back to treating the value as raw text otherwise.
const decodeReport = (data?: string): string => {
  if (!data) return ''
  if (data.startsWith('data') && data.includes('base64,')) {
    return Buffer.from(data.split('base64,')[1], 'base64').toString('utf-8')
  }
  return data
}

// Mirror of DMS extractKositHtml / DocuAI _extract_kosit_html: carve out the embedded <html>…</html>.
const extractKositHtml = (rawReport: string): string => {
  const start = rawReport.indexOf('<html')
  const end = rawReport.indexOf('</html>')
  if (start !== -1 && end !== -1) {
    return rawReport.substring(start, end + '</html>'.length)
  }
  return ''
}

export const DocumentDrawerPreviewHtml = (props: DocumentDrawerPreviewHtmlInterface) => {
  const classes = useStyles()
  const html = useMemo(() => extractKositHtml(decodeReport(props.data)), [props.data])

  // An empty `html` used to render the LOADING text unconditionally, which turned two permanent
  // dead ends into an eternal "Dokument wird geladen..." (reported from the review preview: the
  // validation report is offered in the dropdown but never appears). The three cases are distinct and
  // must be told apart:
  //   1. the parent is still streaming the artifact  → really loading
  //   2. the stream failed (artifact not retrievable yet, or gone) → error, not loading
  //   3. bytes arrived but carry no <html> block (a KoSIT run whose report was not produced with
  //      default-report.xsl) → the report exists but cannot be rendered; saying "loading" hides that
  if (props.hasError) {
    return <div className={classes.empty}>{lang.documents.validationReportUnavailable}</div>
  }
  if (!html) {
    if (props.isLoading || !props.data) {
      return (
        <div className={classes.loaderContainer}>
          <Loader visible={true} text={lang.documents.loadingFile} />
        </div>
      )
    }
    return <div className={classes.empty}>{lang.documents.validationReportNotRenderable}</div>
  }

  return (
    <div className={classes.root}>
      <iframe
        className={classes.frame}
        title={lang.documents.previewCarouselWrapperTabs.validationReport}
        sandbox=""
        srcDoc={html}
      />
    </div>
  )
}

export default DocumentDrawerPreviewHtml
